import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import {
  Search, Sparkles, ArrowRight, Loader2, MapPin, Phone, Globe, Star,
  AlertCircle, Mail, ShieldCheck, AlertTriangle, Target, Pencil, Trash2,
  Zap, X, Check, RotateCcw, Database, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "../components/ConfirmModal";
import { APIFY_RECIPES } from "../lib/apifyRecipes";

const isIcp = (l) =>
  !l.website ||
  (l.website || "").includes("google.com/maps") ||
  l.website_quality === "bad" ||
  l.website_quality === "none";

const labelColor = (label) => ({
  Hot: "#EF4444",
  Warm: "#F59E0B",
  Cold: "#52525B",
  "At Risk": "#A1A1AA",
}[label] || "#71717A");

export default function Scraper() {
  const [source, setSource] = useState("places"); // 'places' | 'apollo' | 'apify'
  const [city, setCity] = useState("Austin");
  const [state, setState] = useState("TX");
  const [niche, setNiche] = useState("wedding venue");
  const [maxResults, setMaxResults] = useState(10);
  const [skipExisting, setSkipExisting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [icpOnly, setIcpOnly] = useState(false);
  const [drafts, setDrafts] = useState([]); // summary list
  const [activeDraft, setActiveDraft] = useState(null); // full draft (with candidates)
  const [selected, setSelected] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [importing, setImporting] = useState(false);
  // Apollo-specific filters
  const [apolloMode, setApolloMode] = useState("organizations"); // 'people' | 'organizations'
  const [apolloTitles, setApolloTitles] = useState("Owner, Marketing Director, CEO");
  const [apolloIndustry, setApolloIndustry] = useState("");
  const [apolloEmpRange, setApolloEmpRange] = useState("11,50"); // Apollo bucket format
  const [apolloKeywords, setApolloKeywords] = useState("");
  // Apify
  const [apifyMode, setApifyMode] = useState("dataset"); // 'dataset' | 'actor'
  const [apifyDatasetId, setApifyDatasetId] = useState("");
  const [apifyActorId, setApifyActorId] = useState("compass/crawler-google-places");
  const [apifyActorInput, setApifyActorInput] = useState(
    '{\n  "searchStringsArray": ["wedding venue Austin TX"],\n  "maxCrawledPlacesPerSearch": 20,\n  "language": "en"\n}',
  );
  const [apifyTimeout, setApifyTimeout] = useState(180);
  const [confirmModal, setConfirmModal] = useState(null);
  const navigate = useNavigate();

  // One-click recipe application: sets mode/actor_id/input/niche/max in one go.
  const applyApifyRecipe = (recipe) => {
    setApifyMode("actor");
    setApifyActorId(recipe.actor_id);
    setApifyActorInput(JSON.stringify(recipe.input, null, 2));
    if (recipe.niche) setNiche(recipe.niche);
    if (recipe.max) setMaxResults(recipe.max);
    toast.success(`Loaded recipe: ${recipe.label}`);
  };

  // ---- Load campaigns + drafts on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaigns(data);
        if (data[0]) setCampaignId(data[0].id);
      } catch (e) { console.error(e); }
      await refreshDrafts();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDrafts = async () => {
    try {
      const { data } = await api.get("/scrape-drafts");
      setDrafts(data);
      // If no active draft yet, load the most recent one with remaining candidates.
      if (!activeDraft && data.length) {
        const newest = data.find((d) => d.remaining_count > 0) || data[0];
        await loadDraft(newest.id);
      }
    } catch (e) { console.error(e); }
  };

  const loadDraft = async (id) => {
    try {
      const { data } = await api.get(`/scrape-drafts/${id}`);
      setActiveDraft(data);
      setSelected(new Set()); // default: empty per user spec
      setEditingId(null);
    } catch (e) {
      toast.error("Couldn't load draft");
    }
  };

  // ---- Run scrape -> creates new draft
  const runScrape = async (e) => {
    e?.preventDefault();
    if (source === "apollo") return runApollo();
    if (source === "apify") return runApify();
    setLoading(true);
    try {
      const payload = { city, state, niche, max_results: maxResults, skip_existing: skipExisting };
      if (campaignId) payload.campaign_id = campaignId;
      const { data } = await api.post("/leads/scrape", payload);
      const skippedTail = data.skipped_existing
        ? ` · skipped ${data.skipped_existing} already-in-pipeline`
        : "";
      if (data.count === 0) {
        toast.info(
          data.message ||
            `0 new candidates${skippedTail}. Try a different city/niche or toggle "Skip duplicates" off.`,
          { duration: 6000 },
        );
        await refreshDrafts();
        return;
      }
      toast.success(`Found ${data.count} new candidates${skippedTail} — review and import`);
      await refreshDrafts();
      await loadDraft(data.draft_id);
      // Scroll to results section
      setTimeout(() => {
        const el = document.getElementById("draft-results");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scrape failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- Apollo B2B search -> creates new draft (same review/import flow)
  const runApollo = async () => {
    setLoading(true);
    try {
      const payload = {
        mode: apolloMode,
        skip_existing: skipExisting,
        max_results: maxResults,
        city: city || null,
        state: state || null,
        industry: apolloIndustry || null,
        niche_label: apolloIndustry || niche || "b2b",
      };
      if (campaignId) payload.campaign_id = campaignId;
      if (apolloMode === "people" && apolloTitles.trim()) {
        payload.person_titles = apolloTitles.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (apolloEmpRange) payload.organization_num_employees_ranges = [apolloEmpRange];
      if (apolloKeywords.trim()) {
        payload.q_organization_keyword_tags = apolloKeywords.split(",").map((s) => s.trim()).filter(Boolean);
      }
      const { data } = await api.post("/leads/apollo-search", payload);
      const skippedTail = data.skipped_existing
        ? ` · skipped ${data.skipped_existing} already-in-pipeline`
        : "";
      if (data.count === 0) {
        toast.info(
          data.message || `0 new Apollo matches${skippedTail}. Broaden filters or check your Apollo API key in Settings.`,
          { duration: 6000 },
        );
        await refreshDrafts();
        return;
      }
      toast.success(`Apollo found ${data.count} new ${apolloMode}${skippedTail} — review and import`);
      await refreshDrafts();
      await loadDraft(data.draft_id);
      setTimeout(() => {
        const el = document.getElementById("draft-results");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Apollo search failed";
      toast.error(msg, { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  // ---- Apify (Dataset import OR sync Actor run) -> creates new draft.
  // We reuse the same draft / review / import pipeline so the UX is
  // identical to Places and Apollo once results come back.
  const runApify = async () => {
    setLoading(true);
    try {
      const isDataset = apifyMode === "dataset";
      const url = isDataset ? "/leads/apify/dataset-import" : "/leads/apify/run-actor";
      const payload = {
        campaign_id: campaignId || null,
        niche_label: niche || "apify",
        default_city: city || null,
        default_state: state || null,
        skip_existing: skipExisting,
        max_results: maxResults,
      };
      if (isDataset) {
        if (!apifyDatasetId.trim()) {
          toast.error("Paste an Apify dataset ID first (from a finished Actor run).");
          return;
        }
        payload.dataset_id = apifyDatasetId.trim();
      } else {
        if (!apifyActorId.trim()) {
          toast.error("Enter an Apify actor ID (e.g. compass/crawler-google-places).");
          return;
        }
        let parsedInput = {};
        if (apifyActorInput.trim()) {
          try {
            parsedInput = JSON.parse(apifyActorInput);
          } catch (err) {
            toast.error("Actor input is not valid JSON — fix the syntax and retry.");
            return;
          }
        }
        payload.actor_id = apifyActorId.trim();
        payload.actor_input = parsedInput;
        payload.timeout_secs = Number(apifyTimeout) || 180;
      }
      const { data } = await api.post(url, payload);
      const skippedTail = data.skipped_existing
        ? ` · skipped ${data.skipped_existing} already-in-pipeline`
        : "";
      if (data.count === 0) {
        toast.info(
          data.message ||
            `0 new Apify matches${skippedTail}. Verify the dataset/actor returned items and that your Apify token is set.`,
          { duration: 7000 },
        );
        await refreshDrafts();
        return;
      }
      toast.success(`Apify imported ${data.count} new candidates${skippedTail} — review and import`);
      await refreshDrafts();
      await loadDraft(data.draft_id);
      setTimeout(() => {
        const el = document.getElementById("draft-results");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Apify import failed";
      toast.error(msg, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  // ---- Computed candidate views
  const candidates = activeDraft?.candidates || [];
  const importedIds = new Set(activeDraft?.imported_candidate_ids || []);
  const visible = useMemo(() => {
    const rest = candidates.filter((c) => !importedIds.has(c.candidate_id));
    return icpOnly ? rest.filter(isIcp) : rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, importedIds, icpOnly]);

  const allVisibleSelected =
    visible.length > 0 && visible.every((c) => selected.has(c.candidate_id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visible.forEach((c) => next.delete(c.candidate_id));
        return next;
      }
      const next = new Set(prev);
      visible.forEach((c) => next.add(c.candidate_id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ---- Inline edit
  const beginEdit = (c) => {
    setEditingId(c.candidate_id);
    setEditForm({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      website: c.website || "",
      address: c.address || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (cid) => {
    try {
      const { data } = await api.patch(
        `/scrape-drafts/${activeDraft.id}/candidate/${cid}`,
        editForm
      );
      setActiveDraft((d) => ({
        ...d,
        candidates: d.candidates.map((c) =>
          c.candidate_id === cid ? { ...c, ...data.candidate } : c
        ),
      }));
      toast.success("Saved");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  // ---- Import / Discard
  const doImport = async (forceSequence) => {
    if (selected.size === 0) {
      toast.error("Select at least one candidate");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post(
        `/scrape-drafts/${activeDraft.id}/import`,
        { selected_ids: Array.from(selected), force_sequence: !!forceSequence }
      );
      if (data.imported === 0 && (data.skipped || []).length > 0) {
        toast.info(`Skipped ${data.skipped.length} candidate(s) — already imported earlier`);
      } else {
        const tail = data.auto_sequence
          ? ` — auto-sequence starts in ${data.first_sms_in_minutes} min`
          : " (no auto-sequence — toggle Sequences in Settings or use force button)";
        toast.success(`Imported ${data.imported} lead(s) to /leads${tail}`);
      }
      await refreshDrafts();
      await loadDraft(activeDraft.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const discardDraft = () => {
    if (!activeDraft) return;
    const remaining = candidates.length - importedIds.size;
    setConfirmModal({
      title: "Discard draft",
      body: `${remaining} un-imported candidate(s) will be deleted permanently.`,
      destructive: true,
      confirmLabel: "Discard",
      onConfirm: async () => {
        try {
          await api.delete(`/scrape-drafts/${activeDraft.id}`);
          toast.success("Draft discarded");
          setActiveDraft(null);
          setSelected(new Set());
          await refreshDrafts();
        } catch (e) {
          toast.error("Failed to discard");
        }
      },
    });
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="scraper-page">
      <div className="border-b border-zinc-200 pb-6 mb-8">
        <div className="overline mb-2">/ ai lead scraper</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Find Local Leads</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Scrape candidates, review with AI scoring, then promote only the ones you want to your Leads pipeline.
        </p>
      </div>

      {/* ---- Scrape form ---- */}
      <form onSubmit={runScrape} className="border border-zinc-200 bg-white p-6 mb-8" data-testid="scrape-form">
        {/* Source tabs — Google Places (local biz) vs Apollo (B2B) vs Apify (marketplace scrapers) */}
        <div className="mb-5 pb-5 border-b border-zinc-100">
          <label className="overline block mb-2">Lead Source</label>
          <div className="grid grid-cols-3 gap-2" data-testid="source-tabs">
            <button
              type="button"
              onClick={() => setSource("places")}
              data-testid="source-tab-places"
              className={`px-4 py-3 text-xs font-bold uppercase tracking-widest border transition flex items-center justify-center gap-2 ${
                source === "places"
                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                  : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
              }`}
            >
              <MapPin className="w-3.5 h-3.5" /> Google Places · Local Biz
            </button>
            <button
              type="button"
              onClick={() => setSource("apollo")}
              data-testid="source-tab-apollo"
              className={`px-4 py-3 text-xs font-bold uppercase tracking-widest border transition flex items-center justify-center gap-2 ${
                source === "apollo"
                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                  : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
              }`}
            >
              <Target className="w-3.5 h-3.5" /> Apollo · B2B Contacts
            </button>
            <button
              type="button"
              onClick={() => setSource("apify")}
              data-testid="source-tab-apify"
              className={`px-4 py-3 text-xs font-bold uppercase tracking-widest border transition flex items-center justify-center gap-2 ${
                source === "apify"
                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                  : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
              }`}
            >
              <Database className="w-3.5 h-3.5" /> Apify · Marketplace
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 font-mono">
            {source === "places" && "Local businesses (venues, salons, restaurants) — pulls from Google Places with website-quality enrichment."}
            {source === "apollo" && "B2B decision-makers and companies — pulls from Apollo.io. Requires Apollo API key in Settings."}
            {source === "apify" && "Run any Apify actor (Google Maps, LinkedIn, Yelp, custom) or import a finished Dataset. Decision-maker contacts auto-mapped on import. Requires Apify API token in Settings."}
          </p>
        </div>

        <div className="mb-5 pb-5 border-b border-zinc-100">
          <label className="overline block mb-2">Attribute to Campaign — for A/B testing</label>
          {campaigns.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="no-campaign-warn">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>No campaigns yet. <button type="button" onClick={() => navigate("/campaign")} className="font-bold underline" data-testid="link-create-campaign">Create one</button> first to track ROI deterministically.</span>
            </div>
          ) : (
            <select
              value={campaignId} onChange={(e) => setCampaignId(e.target.value)}
              data-testid="select-campaign"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
            >
              <option value="">— No attribution —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.target_niche}{c.target_city ? ` / ${c.target_city}` : ""} · ${c.deal_size_usd || 2500}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          {source === "places" && (
            <>
              <div className="md:col-span-4">
                <label className="overline block mb-2">Niche</label>
                <input required value={niche} onChange={(e) => setNiche(e.target.value)}
                  data-testid="input-niche"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="wedding venue, dentist, gym..." />
              </div>
              <div className="md:col-span-3">
                <label className="overline block mb-2">City</label>
                <input required value={city} onChange={(e) => setCity(e.target.value)}
                  data-testid="input-city"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="Austin" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">State</label>
                <input value={state} onChange={(e) => setState(e.target.value)}
                  data-testid="input-state"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="TX" />
              </div>
              <div className="md:col-span-1">
                <label className="overline block mb-2">Max</label>
                <input type="number" min={3} max={25} value={maxResults}
                  onChange={(e) => setMaxResults(Number(e.target.value))}
                  data-testid="input-max"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading}
                  data-testid="btn-scrape"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                  {loading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scraping + Scoring…</>
                    : <><Search className="w-3.5 h-3.5" /> Scrape</>}
                </button>
              </div>
            </>
          )}
          {source === "apollo" && (
            <>
              {/* Apollo mode toggle */}
              <div className="md:col-span-12">
                <label className="overline block mb-2">Search Mode</label>
                <div className="flex gap-2" data-testid="apollo-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setApolloMode("people")}
                    data-testid="apollo-mode-people"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition ${
                      apolloMode === "people"
                        ? "bg-[#002FA7] border-[#002FA7] text-white"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    People (decision-makers)
                  </button>
                  <button
                    type="button"
                    onClick={() => setApolloMode("organizations")}
                    data-testid="apollo-mode-orgs"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition ${
                      apolloMode === "organizations"
                        ? "bg-[#002FA7] border-[#002FA7] text-white"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    Organizations (companies)
                  </button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">
                  {apolloMode === "people"
                    ? "Returns named contacts with title + LinkedIn. Email & phone are HIDDEN by Apollo unless you spend reveal credits."
                    : "Returns companies with industry / size / website / phone — no reveal credits needed. Recommended for cold outbound."}
                </p>
              </div>

              {apolloMode === "people" && (
                <div className="md:col-span-12">
                  <label className="overline block mb-2">Titles (comma-separated)</label>
                  <input
                    value={apolloTitles} onChange={(e) => setApolloTitles(e.target.value)}
                    data-testid="apollo-titles"
                    className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                    placeholder="Owner, Marketing Director, CEO, VP of Sales"
                  />
                </div>
              )}

              <div className="md:col-span-4">
                <label className="overline block mb-2">Industry / Keywords</label>
                <input
                  value={apolloIndustry} onChange={(e) => setApolloIndustry(e.target.value)}
                  data-testid="apollo-industry"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="wedding venues, dentist, photographer..."
                />
                <p className="mt-1 text-[10px] text-zinc-500 leading-tight">
                  Free text — searched as fuzzy keywords across company names & descriptions.
                </p>
              </div>
              <div className="md:col-span-3">
                <label className="overline block mb-2">City</label>
                <input
                  value={city} onChange={(e) => setCity(e.target.value)}
                  data-testid="apollo-city"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="Austin"
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">State</label>
                <input
                  value={state} onChange={(e) => setState(e.target.value)}
                  data-testid="apollo-state"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="Texas"
                />
              </div>
              <div className="md:col-span-3">
                <label className="overline block mb-2">Employees</label>
                <select
                  value={apolloEmpRange}
                  onChange={(e) => setApolloEmpRange(e.target.value)}
                  data-testid="apollo-emp-range"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                >
                  <option value="">Any size</option>
                  <option value="1,10">1–10</option>
                  <option value="11,50">11–50</option>
                  <option value="51,200">51–200</option>
                  <option value="201,500">201–500</option>
                  <option value="501,1000">501–1000</option>
                  <option value="1001,5000">1001–5000</option>
                </select>
              </div>

              <div className="md:col-span-8">
                <label className="overline block mb-2">Keyword tags (optional, comma-separated)</label>
                <input
                  value={apolloKeywords} onChange={(e) => setApolloKeywords(e.target.value)}
                  data-testid="apollo-keywords"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="b2b saas, ecommerce, fintech..."
                />
              </div>
              <div className="md:col-span-1">
                <label className="overline block mb-2">Max</label>
                <input type="number" min={3} max={100} value={maxResults}
                  onChange={(e) => setMaxResults(Number(e.target.value))}
                  data-testid="apollo-max"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
              </div>
              <div className="md:col-span-3">
                <button type="submit" disabled={loading}
                  data-testid="btn-apollo-search"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                  {loading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching Apollo…</>
                    : <><Search className="w-3.5 h-3.5" /> Search Apollo</>}
                </button>
              </div>
            </>
          )}
          {source === "apify" && (
            <>
              {/* Apify mode toggle: Dataset (already-finished run) vs Actor (run now) */}
              <div className="md:col-span-12">
                <label className="overline block mb-2">Apify Mode</label>
                <div className="flex gap-2" data-testid="apify-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setApifyMode("dataset")}
                    data-testid="apify-mode-dataset"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition ${
                      apifyMode === "dataset"
                        ? "bg-[#002FA7] border-[#002FA7] text-white"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    Import Dataset ID
                  </button>
                  <button
                    type="button"
                    onClick={() => setApifyMode("actor")}
                    data-testid="apify-mode-actor"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition ${
                      apifyMode === "actor"
                        ? "bg-[#002FA7] border-[#002FA7] text-white"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    Run Actor (live)
                  </button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">
                  {apifyMode === "dataset"
                    ? "Paste the Dataset ID from a finished Apify run (Console → your run → Dataset tab). Fast — items are already there."
                    : "Launch an Apify actor synchronously. Slower (waits up to your timeout) but no need to leave the CRM."}
                </p>
              </div>

              {/* ──── Apify Recipes: 1-click actor presets ──── */}
              {apifyMode === "actor" && (
                <div className="md:col-span-12">
                  <label className="overline block mb-2 flex items-center gap-1.5">
                    <Wand2 className="w-3 h-3 text-[#002FA7]" />
                    Recipes · 1-click presets
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="apify-recipes">
                    {APIFY_RECIPES.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => applyApifyRecipe(r)}
                        data-testid={`apify-recipe-${r.id}`}
                        title={r.hint}
                        className="group border border-zinc-300 hover:border-[#002FA7] hover:bg-blue-50/40 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-700 hover:text-[#002FA7] flex items-center gap-1.5 transition-colors"
                      >
                        <Sparkles className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">
                    Click a recipe to auto-fill the Actor ID + input JSON below. Then hit "Run Apify Actor" (or tweak first).
                  </p>
                </div>
              )}

              {apifyMode === "dataset" ? (
                <div className="md:col-span-9">
                  <label className="overline block mb-2">Apify Dataset ID</label>
                  <input
                    value={apifyDatasetId}
                    onChange={(e) => setApifyDatasetId(e.target.value)}
                    data-testid="apify-dataset-id"
                    className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono"
                    placeholder="abc123XYZdef456 (open your run on apify.com → Dataset tab → copy the ID)"
                  />
                  <p className="mt-1 text-[10px] text-zinc-500 leading-tight">
                    Works with any actor's output (Google Maps, LinkedIn, Yelp, Apollo Scraper, etc.) — we auto-map common fields and decision-maker contacts.
                  </p>
                </div>
              ) : (
                <>
                  <div className="md:col-span-9">
                    <label className="overline block mb-2">Actor ID</label>
                    <input
                      value={apifyActorId}
                      onChange={(e) => setApifyActorId(e.target.value)}
                      data-testid="apify-actor-id"
                      className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono"
                      placeholder="compass/crawler-google-places"
                    />
                    <p className="mt-1 text-[10px] text-zinc-500 leading-tight">
                      Format: <code>username/actor-name</code>. Find more at <a href="https://apify.com/store" target="_blank" rel="noreferrer" className="underline text-[#002FA7]">apify.com/store</a>.
                    </p>
                  </div>
                  <div className="md:col-span-12">
                    <label className="overline block mb-2">Actor Input (JSON)</label>
                    <textarea
                      rows={6}
                      value={apifyActorInput}
                      onChange={(e) => setApifyActorInput(e.target.value)}
                      data-testid="apify-actor-input"
                      className="w-full px-3 py-2.5 border border-zinc-300 text-xs focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono"
                      placeholder='{"searchStringsArray": ["coffee shops Austin TX"], "maxCrawledPlaces": 20}'
                    />
                    <p className="mt-1 text-[10px] text-zinc-500 leading-tight">
                      Copy the input schema from the actor's page on Apify. Keep <code>maxCrawledPlaces</code> small while testing.
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <label className="overline block mb-2">Timeout (sec)</label>
                    <input
                      type="number" min={30} max={600}
                      value={apifyTimeout}
                      onChange={(e) => setApifyTimeout(Number(e.target.value))}
                      data-testid="apify-timeout"
                      className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                    />
                  </div>
                </>
              )}

              <div className={apifyMode === "dataset" ? "md:col-span-3" : "md:col-span-3"}>
                <label className="overline block mb-2">Max items</label>
                <input
                  type="number"
                  min={1}
                  max={apifyMode === "dataset" ? 10000 : 1000}
                  value={maxResults}
                  onChange={(e) => setMaxResults(Number(e.target.value))}
                  data-testid="apify-max"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                />
              </div>
              <div className="md:col-span-6 grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Default City</label>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="apify-city"
                    className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                    placeholder="Austin (only used when actor doesn't return city)"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Default State</label>
                  <input
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    data-testid="apify-state"
                    className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                    placeholder="TX"
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="overline block mb-2">Niche label</label>
                <input
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  data-testid="apify-niche"
                  className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  placeholder="wedding venue"
                />
              </div>
              <div className="md:col-span-12">
                <button type="submit" disabled={loading}
                  data-testid="btn-apify-search"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                  {loading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {apifyMode === "dataset" ? "Importing…" : "Running actor…"}</>
                    : <><Search className="w-3.5 h-3.5" /> {apifyMode === "dataset" ? "Import Apify Dataset" : "Run Apify Actor"}</>}
                </button>
              </div>
            </>
          )}
        </div>
        <p className="text-[10px] text-zinc-500 mt-3 font-mono flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 cursor-pointer hover:text-zinc-700">
            <input
              type="checkbox"
              checked={skipExisting}
              onChange={(e) => setSkipExisting(e.target.checked)}
              data-testid="input-skip-existing"
              className="w-3 h-3 accent-[#002FA7]"
            />
            <span className="font-bold uppercase tracking-wider">Skip duplicates</span>
            <span className="text-zinc-400 normal-case">
              (don't show businesses already in your leads or pending drafts{campaignId ? " — scoped to selected campaign" : ""})
            </span>
          </label>
          <span className="text-zinc-300">|</span>
          <span>
            Each candidate is enriched (website, emails, socials) and AI-classified before you see it. Nothing is saved to Leads until you click <span className="font-bold">Import</span>.
          </span>
        </p>
      </form>

      {/* ---- Drafts list ---- */}
      {drafts.length > 0 && (
        <div className="mb-6">
          <div className="overline mb-2">/ recent scrape drafts (auto-delete in 24h)</div>
          <div className="flex flex-wrap gap-2">
            {drafts.map((d) => (
              <button
                key={d.id}
                onClick={() => loadDraft(d.id)}
                data-testid={`draft-tab-${d.id}`}
                className={`px-3 py-2 text-xs border flex items-center gap-2 transition ${
                  activeDraft?.id === d.id
                    ? "bg-[#002FA7] border-[#002FA7] text-white"
                    : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                }`}
              >
                <span className="font-bold uppercase tracking-wider">{d.niche}</span>
                <span className="font-mono">· {d.city}</span>
                <span className="font-mono opacity-70">· {d.remaining_count}/{d.candidate_count}</span>
                {d.imported_count > 0 && (
                  <span className="text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1 py-0.5">
                    {d.imported_count} imported
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- Active draft results ---- */}
      {activeDraft && (
        <div id="draft-results" className="border border-zinc-200 bg-white" data-testid="draft-results">
          <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading text-lg font-bold">
                {activeDraft.niche} · {activeDraft.city}{activeDraft.state ? `, ${activeDraft.state}` : ""}
              </div>
              <div className="text-xs text-zinc-500 font-mono mt-0.5">
                {visible.length} shown · {candidates.length - importedIds.size} un-imported · {importedIds.size} already imported · source: <span className="font-bold uppercase">{activeDraft.source}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIcpOnly((v) => !v)}
                data-testid="draft-icp-toggle"
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1.5 ${
                  icpOnly ? "bg-red-600 border-red-600 text-white" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
                }`}>
                <Target className="w-3 h-3" /> ICP Only
              </button>
              <button
                onClick={discardDraft}
                data-testid="btn-discard-draft"
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Discard
              </button>
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-zinc-500">
              {candidates.length === 0
                ? "No candidates in this draft."
                : importedIds.size === candidates.length
                  ? "All candidates already imported. Run a new scrape or discard this draft."
                  : icpOnly
                    ? "No ICP candidates here. Toggle ICP Only off to see all."
                    : "All candidates filtered out."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                      data-testid="draft-select-all"
                      className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 overline">Business</th>
                  <th className="text-left px-4 py-3 overline">Contact</th>
                  <th className="text-left px-4 py-3 overline">Website</th>
                  <th className="text-left px-4 py-3 overline">AI Score</th>
                  <th className="text-right px-4 py-3 overline">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const isEditing = editingId === c.candidate_id;
                  return (
                    <tr key={c.candidate_id}
                        className="border-b border-zinc-100 align-top hover:bg-zinc-50/50"
                        data-testid={`cand-row-${c.candidate_id}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(c.candidate_id)}
                          onChange={() => toggleOne(c.candidate_id)}
                          data-testid={`cand-select-${c.candidate_id}`}
                          className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            data-testid={`edit-name-${c.candidate_id}`}
                            className="w-full px-2 py-1 text-sm border border-[#002FA7] font-semibold"
                          />
                        ) : (
                          <>
                            <div className="font-semibold flex items-center gap-1.5">
                              {c.name}
                              {isIcp(c) && (
                                <span className="text-[9px] px-1 py-0.5 bg-red-100 text-red-700 font-bold uppercase tracking-wider">ICP</span>
                              )}
                              {c.phone_verified && (
                                <span title="Phone verified via Google Places">
                                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
                              <MapPin className="w-3 h-3" /> {c.city || activeDraft.city}{c.state ? `, ${c.state}` : ""}
                            </div>
                            {c.rating && (
                              <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {c.rating} ({c.reviews || 0})
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              value={editForm.phone}
                              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                              data-testid={`edit-phone-${c.candidate_id}`}
                              placeholder="phone"
                              className="w-full px-2 py-1 text-xs border border-zinc-300 font-mono"
                            />
                            <input
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              data-testid={`edit-email-${c.candidate_id}`}
                              placeholder="email"
                              className="w-full px-2 py-1 text-xs border border-zinc-300 font-mono"
                            />
                          </div>
                        ) : (
                          <>
                            {c.phone
                              ? <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>
                              : <div className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" /> no phone</div>}
                            {c.email && <div className="flex items-center gap-1 text-zinc-500 mt-0.5"><Mail className="w-3 h-3" /> {c.email}</div>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs max-w-[200px]">
                        {isEditing ? (
                          <input
                            value={editForm.website}
                            onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                            data-testid={`edit-website-${c.candidate_id}`}
                            placeholder="website"
                            className="w-full px-2 py-1 text-xs border border-zinc-300 font-mono"
                          />
                        ) : c.website ? (
                          <div className="flex items-center gap-1 truncate">
                            <Globe className="w-3 h-3 shrink-0" />
                            <span className="truncate">{c.website.replace(/^https?:\/\//, "")}</span>
                            {c.website_quality === "bad" && (
                              <span className="text-[9px] px-1 bg-amber-100 text-amber-800 font-bold">BAD</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> none</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {c.ai_label ? (
                          <div className="flex items-center gap-1.5">
                            <span
                              className="px-1.5 py-0.5 text-[9px] font-bold uppercase text-white"
                              style={{ background: labelColor(c.ai_label) }}
                            >
                              {c.ai_label}
                            </span>
                            <span className="font-mono text-xs font-bold">{c.ai_score}</span>
                          </div>
                        ) : c.classify_error ? (
                          <span className="text-[10px] text-red-600 font-mono" title={c.classify_error}>err</span>
                        ) : (
                          <span className="text-xs text-zinc-400 font-mono">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => saveEdit(c.candidate_id)}
                              data-testid={`btn-save-edit-${c.candidate_id}`}
                              className="text-emerald-700 hover:text-emerald-800"
                              title="Save"><Check className="w-4 h-4" /></button>
                            <button
                              onClick={cancelEdit}
                              data-testid={`btn-cancel-edit-${c.candidate_id}`}
                              className="text-zinc-400 hover:text-red-600"
                              title="Cancel"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <button
                            onClick={() => beginEdit(c)}
                            data-testid={`btn-edit-${c.candidate_id}`}
                            className="text-zinc-400 hover:text-[#002FA7] transition-colors"
                            title="Edit before importing">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Sticky action bar */}
          {visible.length > 0 && (
            <div className="px-5 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-zinc-600">
                <span className="font-bold text-[#002FA7]">{selected.size}</span> of {visible.length} selected
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => doImport(false)}
                  disabled={importing || selected.size === 0}
                  data-testid="btn-import"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition disabled:opacity-50 flex items-center gap-1.5"
                  title="Promote selected to Leads. Auto-sequence only triggers if you enabled it in Settings.">
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  Import {selected.size > 0 ? `(${selected.size})` : ""}
                </button>
                <button
                  onClick={() => doImport(true)}
                  disabled={importing || selected.size === 0}
                  data-testid="btn-import-force"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-black hover:bg-zinc-800 text-white transition disabled:opacity-50 flex items-center gap-1.5"
                  title="Promote selected AND start the AI SMS + call sequence right away (overrides the Settings toggle for this batch).">
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  Import + Auto-Contact {selected.size > 0 ? `(${selected.size})` : ""}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help bubble when no drafts at all */}
      {drafts.length === 0 && !activeDraft && (
        <div className="border border-dashed border-zinc-300 bg-white px-6 py-8 text-center text-sm text-zinc-500">
          Run your first scrape above to see candidates here. AI scoring takes a few seconds per result.
        </div>
      )}
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}
